// V2 版式：cards（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/cards.ts 逐坐标移植
// 3–4 张等权卡，供料 cards block（title/desc）。
// 瑞士：grey1 灰底直角卡，仅第一张 accent 满铺；杂志：无底色，卡间距中线画竖 hairline 分栏。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { luxeExt } from "./luxe";
import {
  consultTheme, brutalTheme, termTheme, reportTheme, inkTheme, memphisTheme, memphisPalette,
} from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2Cards({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  // 与后端 cards.ts GEO 同源（deno dump 生成，见 v2geo.generated.ts 头部注释）
  const GEO = V2GEO["cards"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // 卡底色：瑞士取主题 grey1（后端 cards.ts:37；杂志无底色，不消费此值）
  const theme = skin.theme as { grey?: { grey1?: string } };
  const grey1 = theme.grey?.grey1 ?? skin.hairline;
  // luxe 黑卡配色（cards.ts 分支）：ink 底 + 米白文字 + 灰一次要文字；hero 卡为唯一裸金卡（accentLight 底）
  const ext = luxeExt(skin);
  // 主题库扩展 6 风格（044 T3.3）：卡底/边框/文字用色分支（cards.ts:82-174 逐风格移植）
  const consultT = consultTheme(skin);
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);

  const bi = c.blocks.findIndex((b) => b.type === "cards");
  const b = bi >= 0 ? c.blocks[bi] : null;
  const items = (b && b.type === "cards" ? b.items : []).slice(0, 4);

  const n = items.length;
  const gap = SPACING.cardGap;
  const w = n > 0 ? (W - 2 * m - (n - 1) * gap) / n : 0;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "cards")} page={page} total={total} foot={footOf()} />

      {/* 页标题 */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {/* 副标题 */}
      {(c.subtitle || editable) && (
        <EditableText
          value={c.subtitle ?? ""} path="subtitle" editable={editable}
          onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
          style={{
            position: "absolute", left: px(m), top: px(GEO.subY), width: px(W - 2 * m - 2), height: px(GEO.subH),
            fontSize: pt(skin.scale.body), color: skin.sub, fontFamily: skin.fontBody,
          }}
        />
      )}

      {items.map((it, i) => {
        const x = m + i * (w + gap);
        // 单一 accent 只给第一张卡（luxe 为唯一裸金卡）；ink/memphis 无 hero 卡（分栏无底色 / 5 色轮用）
        const hero = i === 0 && !mag && !inkT && !mpT;
        // memphis 头带色（5 色轮用）与头带文字色（黄浅底黑字，其余白字——文字自动黑白对比）
        const headerColor = mpT ? memphisPalette(mpT)[i % 5] : "";
        const onHeader = i % 5 === 4 ? (mpT ? mpT.ink : "") : "#FFFFFF";
        // 卡底/边框分支（cards.ts:82-142 逐风格移植）
        const bgColor = consultT ? (hero ? skin.accent : grey1)
          : brutalT ? brutalT.paper
            : termT ? "transparent"
              : reportT ? reportT.inkDeep
                : mpT ? "#FFFFFF"
                  : !mag ? (hero ? (ext ? ext.accentLight : skin.accent) : (ext ? ext.ink : grey1))
                    : "transparent";
        const border = brutalT
          ? `3px solid ${hero ? brutalT.accent : brutalT.ink}`
          : termT ? `1px solid ${hero ? termT.accent : termT.muted}`
            : reportT ? `1px solid ${hero ? reportT.accent : reportT.muted}`
              : "none";
        const radius = (consultT || mpT) ? px(skin.radius) : undefined;
        // 序号/标题/描述用色（cards.ts:152-174）
        const sem = ["accentEmerald", "accentGold", "accentCoral", "accentSlate"] as const;
        const num = reportT ? (hero ? reportT.accent : reportT.muted)
          : inkT ? inkT.accent
            : mpT ? onHeader
              : hero ? (termT ? skin.accent : skin.accentOn)
                : consultT ? (consultT as unknown as Record<string, string>)[sem[(i - 1) % 4]] ?? skin.accent
                  : brutalT ? brutalT.muted
                    : skin.accent;
        const titleColor = reportT ? (hero ? reportT.accent : reportT.ink)
          : mpT ? onHeader
            : hero && brutalT ? brutalT.accent
              : hero && termT ? skin.text
                : hero ? skin.accentOn : ext ? ext.paper : skin.text;
        const subColor = reportT ? reportT.muted
          : mpT ? mpT.muted
            : hero && brutalT ? brutalT.ink
              : hero && termT ? skin.sub
                : hero ? skin.accentOn : ext ? ext.grey1 : skin.sub;
        return (
          <div key={i}>
            {/* 卡底（cards.ts 分支）；mag/ink 无底色，仅竖分栏线 */}
            {bgColor !== "transparent" && (
              <div style={{
                position: "absolute", left: px(x), top: px(GEO.cardY), width: px(w), height: px(GEO.cardH),
                background: bgColor, borderRadius: radius, border,
              }} />
            )}
            {mag && i > 0 && (
              <div style={{
                position: "absolute", left: px(x - gap / 2), top: px(GEO.cardY), width: 1, height: px(GEO.cardH),
                background: skin.hairline,
              }} />
            )}
            {/* ink（043 §4.6）：无底色卡 + 黛灰细发线竖分栏（保留白） */}
            {inkT && i > 0 && (
              <div style={{
                position: "absolute", left: px(x - gap / 2), top: px(GEO.cardY), width: 1, height: px(GEO.cardH),
                background: inkT.accentGrey,
              }} />
            )}
            {/* memphis（043 §4.7）：彩头带（5 色轮用）+ 补块修平底角（只圆顶部两角） */}
            {mpT && (
              <>
                <div style={{
                  position: "absolute", left: px(x), top: px(GEO.cardY), width: px(w), height: px(0.5),
                  background: headerColor, borderRadius: `${px(skin.radius)} ${px(skin.radius)} 0 0`,
                }} />
                <div style={{
                  position: "absolute", left: px(x), top: px(GEO.cardY + 0.32), width: px(w), height: px(0.18),
                  background: headerColor,
                }} />
              </>
            )}
            {/* luxe 金条标题装饰（accent 细条，cards.ts 分支） */}
            {ext && (
              <div style={{
                position: "absolute", left: px(x + GEO.padX), top: px(GEO.cardY + GEO.ctY - 0.16),
                width: px(0.42), height: px(0.035),
                background: hero ? skin.accentOn : skin.accent,
              }} />
            )}
            {/* 序号（等宽小字，静态装饰）；memphis 序号进头带 */}
            <div style={{
              position: "absolute", left: px(x + GEO.padX), top: px(mpT ? GEO.cardY + 0.1 : GEO.cardY + GEO.numY),
              width: px(1.2), height: px(0.3),
              fontSize: pt(skin.scale.caption), color: num, fontFamily: skin.fontMono, letterSpacing: "0.15em",
            }}>{`0${i + 1}`}</div>
            {/* 卡片标题；memphis 标题进头带 */}
            <EditableText
              value={it.title} path={`blocks.${bi}.items.${i}.title`} editable={editable} multiline
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x + GEO.padX),
                top: px(mpT ? GEO.cardY + 0.08 : GEO.cardY + GEO.ctY),
                width: px(w - 2 * GEO.padX), height: px(mpT ? 0.4 : GEO.ctH),
                fontSize: pt(skin.scale.sectionHead), color: titleColor, fontFamily: skin.fontBody,
                fontWeight: 700, lineHeight: 1.15,
              }}
            />
            {/* 描述 / bullets（048 F4：枚举 bullet 分条 + 长文本分档 autofit，与后端 cards.ts 同源） */}
            {(() => {
              const bullets = (it as { bullets?: string[] }).bullets;
              const boxH = GEO.cardH - GEO.ddY - GEO.ddPad;
              const boxStyle = {
                position: "absolute" as const, left: px(x + GEO.padX), top: px(GEO.cardY + GEO.ddY),
                width: px(w - 2 * GEO.padX), height: px(boxH),
              };
              if (bullets?.length) {
                const totalChars = bullets.join("").length;
                const fs = bullets.length > 4 || totalChars > 96
                  ? Math.max(9, skin.scale.caption - 2.5)
                  : Math.max(10, skin.scale.caption - 1.5);
                return (
                  <div style={{ ...boxStyle, color: subColor, fontFamily: skin.fontBody }}>
                    {bullets.map((t, bi) => (
                      <p key={bi} style={{ margin: 0, fontSize: pt(fs), lineHeight: 1.25 }}>{`· ${t}`}</p>
                    ))}
                  </div>
                );
              }
              if (!it.desc && !editable) return null;
              const len = (it.desc ?? "").length;
              const fs = len <= 60 ? skin.scale.caption : len <= 120 ? Math.max(10, skin.scale.caption - 1.5) : Math.max(9, skin.scale.caption - 2.5);
              return (
                <EditableText
                  value={it.desc ?? ""} path={`blocks.${bi}.items.${i}.desc`} editable={editable} multiline
                  onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                  style={{ ...boxStyle, fontSize: pt(fs), color: subColor, fontFamily: skin.fontBody, lineHeight: 1.35 }}
                />
              );
            })()}
          </div>
        );
      })}
    </>
  );
}
